import { fetchConfig } from "@theme/utilities";
import {
  CartAddInitiatedEvent,
  CartAddEvent,
  CartErrorEvent,
} from "@theme/events";

/**
 * @class Cart
 * @description A utility class providing static methods for interacting with the theme's shopping cart.
 */
export class Cart {
  /**
   * Creates a FormData instance from a plain JavaScript object.
   * This method handles boolean values by converting them to string representations ("true" or "false")
   * and skips `null` and `undefined` values.
   *
   * @static
   * @param {object} data The plain JavaScript object to convert into FormData.
   * @returns {FormData} A FormData instance populated with the object's data.
   */
  static createFormDataFromObject(data) {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      // Skip null and undefined values
      if (value === null || value === undefined) {
        return;
      }

      // If the value is a boolean, put in text attribute value
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }

      // Handle all other types (string, number)
      formData.append(key, value);
    });

    return formData;
  }

  /**
   * Asynchronously adds an item to the cart using the Shopify AJAX API.
   * It dispatches a series of custom events to signal the state of the operation
   * (`CartAddInitiatedEvent`, `CartAddEvent`, `CartErrorEvent`).
   *
   * This method also supports Shopify's section rendering by collecting section IDs
   * from `cart-items-component` elements on the page.
   *
   * @static
   * @async
   * @param {string | number} identifier A unique identifier for the component or context initiating the action.
   * @param {FormData} formData The FormData object containing cart item details (e.g., 'id', 'quantity', 'product-id').
   * @param {object} [options={}] Optional configuration and callbacks.
   * @param {(response: object) => void} [options.successCallback] A callback executed on a successful API response. Receives the JSON response.
   * @param {(errorResponse: object) => void} [options.errorCallback] A callback executed on a failed API response. Receives the JSON error response.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  static async add(
    identifier,
    formData,
    { successCallback, errorCallback } = {},
  ) {
    const variantId = formData.get("id");
    const productId = formData.get("product-id");
    const productHandle = formData.get("product-handle");
    const quantity = Number(formData.get("quantity") || 1);
    const hasCrossSellRedirect =
      formData.get("product-cross-sell-redirect") === "true";

    document.dispatchEvent(
      new CartAddInitiatedEvent({}, identifier, {
        source: "sdk",
        itemCount: quantity,
        productId,
      }),
    );

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const defaultEventObject = {
      source: "sdk",
      itemCount: quantity,
      productId,
      productHandle,
      variantId,
      hasCrossSellRedirect,
    };

    try {
      const res = await fetch(Theme.routes.root + "cart/add.js", {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "text/html",
        },
      });

      const response = await res.json();

      // Error handling via response
      if (response.status) {
        document.dispatchEvent(
          new CartErrorEvent(identifier, response.message),
        );

        if (errorCallback) {
          errorCallback(response);
        }

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        document.dispatchEvent(
          new CartAddEvent({}, identifier, {
            didError: true,
            ...defaultEventObject,
          }),
        );
      } else {
        if (successCallback) {
          successCallback(response);
        }

        if (!variantId) throw new Error("Form variant ID is required");

        document.dispatchEvent(
          new CartAddEvent({}, variantId.toString(), defaultEventObject),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }
}
